import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Moment } from "moment";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import { userSlice } from "../slices/UserSlice";
import { workSlice } from "../slices/WorkSlice";
import { workTasksSlice } from "../slices/WorkTasksSlice";

export type TimerStatus = "started" | "paused" | "ended";
export type TimerTargetType = "work" | "workTask";

export type TimerWorkTask = {
  id: string;
  name: string;
};

type StartTimerArgs = {
  workId: string | null;
  customerId: string;
  assignedUserId: string;
  jobTaskId?: string | null;
  workTask?: TimerWorkTask | null;
  work?: ITimer["work"] | null;
  customerName?: string;
};

export interface ITimer {
  id: string;
  workId: string;
  customerId: string;
  duration: number;
  timerStatus: TimerStatus;
  workTypeId: string | null;
  /** Set by the backend from the work type's default when the timer starts. */
  isBillable?: boolean;
  targetType?: TimerTargetType;
  jobTaskId?: string | null;
  workTask?: TimerWorkTask | null;
  work: any;
  customerName: string;
  contactName: string;
  businessContactId: string;
  contactId: string;
  assignedUserId: string;
  notes: string;
  assignedUser: any;
  contact: any;
  businessContact: any;
}

type StoredTimerState = {
  timer: ITimer | null;
  duration: number;
  updatedAt: number;
};

const TIMER_STORAGE_KEY = "proc_active_timer";

const getStoredTimerDuration = (
  timer: ITimer | null,
  duration: number,
  updatedAt: number,
): number => {
  const safeDuration = Number.isFinite(duration) ? Math.max(duration, 0) : 0;

  if (timer?.timerStatus !== "started" || !Number.isFinite(updatedAt)) {
    return safeDuration;
  }

  const elapsedSeconds = Math.max(
    Math.floor((Date.now() - updatedAt) / 1000),
    0,
  );

  return safeDuration + elapsedSeconds;
};

export const getStoredTimerState = (): Pick<
  StoredTimerState,
  "timer" | "duration"
> => {
  if (typeof window === "undefined") {
    return { timer: null, duration: 0 };
  }

  try {
    const storedState = window.localStorage.getItem(TIMER_STORAGE_KEY);

    if (!storedState) {
      return { timer: null, duration: 0 };
    }

    const parsedState = JSON.parse(storedState) as StoredTimerState;
    const timer = parsedState.timer
      ? normalizeTimerResponse(parsedState.timer)
      : null;
    const duration = getStoredTimerDuration(
      timer,
      Number(parsedState.duration) || 0,
      Number(parsedState.updatedAt),
    );

    return { timer, duration };
  } catch {
    return { timer: null, duration: 0 };
  }
};

export const setStoredTimerState = (
  timer: ITimer | null,
  duration: number,
): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (!timer?.timerStatus) {
      window.localStorage.removeItem(TIMER_STORAGE_KEY);
      return;
    }

    const storedState: StoredTimerState = {
      timer,
      duration,
      updatedAt: Date.now(),
    };

    window.localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(storedState));
  } catch {
    // Ignore storage failures; the server sync still restores timer state.
  }
};

function normalizeTimerResponse(
  timer: ITimer,
  fallbackTimer?: ITimer | null,
): ITimer {
  const jobTaskId = timer.jobTaskId ?? fallbackTimer?.jobTaskId ?? null;
  const workTask = timer.workTask ?? fallbackTimer?.workTask ?? null;
  const work = timer.work ?? fallbackTimer?.work ?? null;
  const customerName = timer.customerName ?? fallbackTimer?.customerName ?? "";

  return {
    ...timer,
    duration: Number(timer.duration) || 0,
    targetType: jobTaskId ? "workTask" : timer.targetType || "work",
    jobTaskId,
    workTask,
    work,
    customerName,
  };
}

const getSyncedTimerDuration = (
  timer: ITimer,
  currentTimer: ITimer | null,
  currentDuration: number,
): number => {
  const nextDuration = Number(timer.duration) || 0;

  if (timer.id === currentTimer?.id && nextDuration < currentDuration) {
    return currentDuration;
  }

  return nextDuration;
};
interface IInitialState {
  timer: ITimer | null;
  duration: number;
  isLoading: boolean;
  error: string;
}
const initialState: IInitialState = {
  ...getStoredTimerState(),
  isLoading: false,
  error: "",
};

export const endTimer = createAsyncThunk(
  "timer/endTimer",
  async (
    body: {
      id: string;
      duration: number;
      date: Moment;
      workId: string | null;
      notes: string;
      assignedUserId: string | null;
      workTypeId: string | null;
      targetType?: TimerTargetType;
      jobTaskId?: string | null;
      isBillable?: boolean;
    },
    { dispatch, rejectWithValue },
  ) => {
    const response = await fetch(
      `${devConfig.getServerBaseUrlForEnv()}/api/time/end-timer`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        body: JSON.stringify(body),
        method: "POST",
      },
    );
    if (response.ok) {
      const data = await response.json();
      dispatch(userSlice.util.invalidateTags(["Time"]));
      dispatch(workSlice.util.invalidateTags(["Work"]));
      dispatch(workTasksSlice.util.invalidateTags(["WorkTask"]));
      return data;
    } else {
      return rejectWithValue("Error");
    }
  },
);

export const pauseTimer = createAsyncThunk(
  "timer/pauseTimer",
  async (timeId: string, { rejectWithValue }) => {
    const response = await fetch(
      `${devConfig.getServerBaseUrlForEnv()}/api/time/pause-timer?timeId=${timeId}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        method: "PUT",
      },
    );
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      return rejectWithValue("Error");
    }
  },
);

export const startTimer = createAsyncThunk(
  "timer/startTimer",
  async (
    {
      workId,
      customerId,
      assignedUserId,
      jobTaskId,
      workTask,
      work,
      customerName,
    }: StartTimerArgs,
    { rejectWithValue },
  ) => {
    const queryParams = new URLSearchParams({
      customerId,
      assignedUserId,
    });

    if (jobTaskId) {
      queryParams.set("workTaskId", jobTaskId);
    } else {
      queryParams.set("workId", String(workId));
    }

    const startTimerPath = jobTaskId
      ? "api/time/start-subtask-timer"
      : "api/time/start-timer";

    const response = await fetch(
      `${devConfig.getServerBaseUrlForEnv()}/${startTimerPath}?${queryParams.toString()}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        method: "GET",
      },
    );
    if (response.ok) {
      const data = await response.json();
      return normalizeTimerResponse({
        ...data,
        workId: data.workId ?? workId,
        targetType: jobTaskId ? "workTask" : data.targetType || "work",
        jobTaskId: jobTaskId ?? data.jobTaskId ?? null,
        workTask: workTask ?? data.workTask ?? null,
        work: data.work ?? work ?? null,
        customerName: data.customerName ?? customerName ?? "",
      });
    } else {
      return rejectWithValue("Error");
    }
  },
);
export const elapsedTimer = createAsyncThunk(
  "timer/allTimers",
  async (
    { orgId, userId }: { orgId: string; userId: string },
    { rejectWithValue },
  ) => {
    const response = await fetch(
      `${devConfig.getServerBaseUrlForEnv()}/api/time/all-timers?orgId=${orgId}&userId=${userId}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        method: "GET",
      },
    );
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      return rejectWithValue("Error");
    }
  },
);

export const timerSlice = createSlice({
  name: "timer",
  initialState,
  reducers: {
    setTimer(state, action: PayloadAction<ITimer | null>) {
      state.timer = action.payload;
    },
    setTimerDuration(state, action: PayloadAction<number>) {
      if (state.timer?.timerStatus) {
        state.duration = action.payload;
      }
    },
    increaseDuration(state) {
      if (state.timer?.timerStatus) {
        state.duration = state.duration + 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startTimer.fulfilled, (state, action) => {
        const timer = normalizeTimerResponse(action.payload);

        state.timer = timer;
        state.duration = timer.duration;
      })
      .addCase(pauseTimer.fulfilled, (state, action) => {
        const timer = normalizeTimerResponse(action.payload, state.timer);

        state.timer = timer;
        state.duration = timer.duration;
      })
      .addCase(elapsedTimer.fulfilled, (state, action) => {
        if (action.payload?.timerStatus) {
          const timer = normalizeTimerResponse(action.payload, state.timer);
          const duration = getSyncedTimerDuration(
            timer,
            state.timer,
            state.duration,
          );

          state.timer = timer;
          state.duration = duration;
        } else {
          state.timer = null;
          state.duration = 0;
        }
      });
  },
});

export const { setTimerDuration, setTimer, increaseDuration } =
  timerSlice.actions;
export default timerSlice.reducer;
